import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LIBRARY_TABS,
  LibraryTab,
  libraryDraftPath,
  libraryPodcastPath,
  libraryTabPath,
  parseAppLocation,
} from '../routing/appRoutes';
import { ignorePlainClick } from '../routing/linkClick';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Drawer,
  List,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Podcasts as PodcastsIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Article as ArticleIcon,
  Restaurant as RestaurantIcon,
  Movie as MovieIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { api } from '../api/apiService';
import { NewsCluster, WikiConcept } from '../types/types';
import { NotebooksContext, FrontendNotebook, ContrastingView } from '../contexts/NotebooksContext';
import { PodcastsContext, PodcastData } from '../contexts/PodcastsContext';
import { UserContext } from '../contexts/UserContext';
import { AnalyticProfileContext } from '../contexts/AnalyticProfileContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LibraryClusterInsightsDrawer from '../components/draftCreator/LibraryClusterInsightsDrawer';
import { mergeClusterArticleHtmlIntoArticleDraft } from '../utils/mergeClusterArticleIntoPortalDraft';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import NotebookListItem from '../components/NotebookListItem';
import PodcastListItem from '../components/PodcastListItem';
import {
  inferNotebookDraftKind,
  mainDraftTabToKind,
  qualifiesAsLibraryArticleListItem,
  setNotebookDraftKind,
  peekNotebookToOpen,
  clearNotebookToOpen,
  removeNotebookDraftKind,
} from '../utils/draftNotebookKind';
import {
  clusterDraftGenerationLoadingBody,
  clusterDraftGenerationLoadingTitle,
} from '../utils/clusterDraftGenerationCopy';
import { markNotebookDraftLifetimeSave } from '../utils/notebookDraftLifetimeSave';
import {
  alignEnvelopeWithNotebookNameIfPlaceholder,
  ensurePortalEnvelope,
  isDiscardableEmptyLibraryDraft,
  patchOrEnsurePortalPrimaryTitle,
  primaryTitleFromEnvelope,
  serializePortalDraft,
  tryParsePortalDraft,
} from '../portal/portalDraftEnvelope';
import { recipePayloadMeetsMandatoryFields } from '../utils/recipeDraftHelpers';
import { StructuredDraftPanel } from '../components/draftCreator/StructuredDraftPanel';
import { ArticlePortalDraftPanel } from '../components/draftCreator/ArticlePortalDraftPanel';
import { DraftAccessBar } from '../components/draftCreator/DraftAccessBar';
import { LibraryDrawerTitleChrome } from '../components/draftCreator/LibraryDrawerTitleChrome';
import { PodcastPortalDraftPanel } from '../components/draftCreator/PodcastPortalDraftPanel';
import { GeneratePodcastFromClusterDialog } from '../components/draftCreator/GeneratePodcastFromClusterDialog';
import type { PortalPodcastEpisodePayload } from '../portal/podcastEpisodeEnvelope';
import {
  parseOrDefaultPodcastEpisode,
  serializePodcastEpisodeDraft,
  serializePodcastEpisodeDraftForSave,
} from '../portal/podcastEpisodeEnvelope';
// Register the chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

function resolvePodcastClustersFromLibrary(
  pod: PodcastData,
  notebooks: FrontendNotebook[]
): NewsCluster[] {
  if (pod.clusters && pod.clusters.length > 0) return pod.clusters;
  const ids = new Set((pod.cluster_ids || []).map(String));
  if (ids.size === 0) return [];
  const out: NewsCluster[] = [];
  const seen = new Set<string>();
  for (const nb of notebooks) {
    for (const cl of nb.clusters || []) {
      const ref = ((cl.cluster_id || '') as string).trim() || cl.title;
      if (ids.has(ref) && !seen.has(cl.title)) {
        seen.add(cl.title);
        out.push({
          ...cl,
          articles: cl.articles || [],
          suggested_titles: cl.suggested_titles || [],
          social_media: cl.social_media || [],
          contrasting_views: cl.contrasting_views || [],
          wiki_concepts: cl.wiki_concepts || [],
          timeline: cl.timeline || [],
        });
      }
    }
  }
  return out;
}

function mergePodcastInsightClusters(pod: PodcastData | null, notebooks: FrontendNotebook[]): NewsCluster[] {
  if (!pod) return [];
  const byTitle = new Map<string, NewsCluster>();
  for (const c of pod.clusters || []) {
    byTitle.set(c.title, c);
  }
  for (const lid of pod.linked_notebook_ids || []) {
    const nb = notebooks.find((n) => n.id === lid);
    if (!nb?.clusters?.length) continue;
    for (const cl of nb.clusters) {
      if (byTitle.has(cl.title)) continue;
      byTitle.set(cl.title, {
        ...cl,
        articles: cl.articles || [],
        suggested_titles: cl.suggested_titles || [],
        social_media: cl.social_media || [],
        contrasting_views: cl.contrasting_views || [],
        wiki_concepts: cl.wiki_concepts || [],
        timeline: cl.timeline || [],
      });
    }
  }
  return Array.from(byTitle.values());
}

// Define notebook section tabs
type NotebookSection = 'active' | 'archived' | 'trash';
// Define podcast section tabs
type PodcastSection = 'active' | 'archived' | 'trash';

/** Label + icon of each Library tab; the tab bar is built from LIBRARY_TABS so links and selection share one order. */
const LIBRARY_TAB_LABELS: Record<LibraryTab, { Icon: typeof ArticleIcon; label: string }> = {
  articles: { Icon: ArticleIcon, label: 'Articles' },
  podcasts: { Icon: PodcastsIcon, label: 'Podcasts' },
  recipes: { Icon: RestaurantIcon, label: 'Recipes' },
  movies: { Icon: MovieIcon, label: 'Movies' },
  events: { Icon: EventIcon, label: 'Events' },
};

/** Library tab index (see LIBRARY_TABS) for each draft kind. */
const TAB_FOR_DRAFT_KIND: Record<ReturnType<typeof inferNotebookDraftKind>, number> = {
  article: LIBRARY_TABS.indexOf('articles'),
  recipe: LIBRARY_TABS.indexOf('recipes'),
  movie: LIBRARY_TABS.indexOf('movies'),
  event: LIBRARY_TABS.indexOf('events'),
};

const LayoutLibrary = () => {
  const { 
    notebooks: notebooksFromContext, 
    hasLoaded: notebooksHaveLoaded,
    isLoading: notebooksLoading,
    fetchNotebooks,
    addNotebook: createNotebook, 
    updateNotebook, 
    updateDraftContent,
    markDraftReadyForPublishing,
    deleteNotebook,
    isClusterContentGenerating,
    getActiveNotebooks,
    addClustersToNotebook,
    removeClustersFromNotebook,
    generateClusterContent,
  } = useContext(NotebooksContext);
  
  const {
    podcasts,
    isLoading,
    hasLoaded: podcastsHaveLoaded,
    fetchPodcasts,
    generatePodcast,
    updatePodcast,
    changePodcastStatus,
    deletePodcast,
    createBlankPodcast,
    addClustersToPodcast,
    removeClustersFromPodcast,
    error: podcastContextError,
    setError: setPodcastContextError
  } = useContext(PodcastsContext);

  const { user } = useContext(UserContext);
  const { availableProfiles } = useContext(AnalyticProfileContext);
  const creatorDisplayName =
    user && (user.first_name || user.last_name)
      ? `${user.first_name} ${user.last_name}`.trim()
      : user?.username || 'Signed-in user';
  
  /** 0 Articles, 1 Podcasts, 2 Recipes, 3 Movies, 4 Events */
  const [mainDraftTab, setMainDraftTab] = useState<number>(0);
  const [selectedSection, setSelectedSection] = useState<NotebookSection>('active');
  const [selectedPodcastSection, setSelectedPodcastSection] = useState<PodcastSection>('active');
  
  // Selected notebook for viewing details
  const [selectedNotebook, setSelectedNotebook] = useState<FrontendNotebook | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  
  // Snackbar for notifications
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');

  // Add state for selected cluster details
  const [selectedClusterForDetails, setSelectedClusterForDetails] = useState<NewsCluster | null>(null);
  
  // Incorrect articles state
  const [incorrectArticles, setIncorrectArticles] = useState<string[]>([]);
  const [articleFeedbackSuccess, setArticleFeedbackSuccess] = useState('');
  const [articleFeedbackError, setArticleFeedbackError] = useState('');
  const [showArticleFeedbackAlert, setShowArticleFeedbackAlert] = useState(false);

  /** Single cluster selected for article draft generation (Insights → Related clusters). */
  const [draftGenClusterTitle, setDraftGenClusterTitle] = useState<string | null>(null);
  /** Single cluster selected for podcast episode generation (Insights → Related clusters). */
  const [podcastGenClusterTitle, setPodcastGenClusterTitle] = useState<string | null>(null);
  const [generatePodcastDialogOpen, setGeneratePodcastDialogOpen] = useState(false);
  const [articleClusterGenBusy, setArticleClusterGenBusy] = useState(false);
  const [isPodcastGenerating, setIsPodcastGenerating] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState<PodcastData | null>(null);
  const [showPodcastContent, setShowPodcastContent] = useState(false);

  // Podcast editing (drawer body — title in header)
  const [libraryPodcastTitleEdit, setLibraryPodcastTitleEdit] = useState(false);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [editedBody, setEditedBody] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [podcastEpisodePayload, setPodcastEpisodePayload] = useState<PortalPodcastEpisodePayload | null>(null);

  // Add loading state for notebook details
  const [notebookClustersLoading] = useState<boolean>(false);

  // Add state to track if sections have been loaded
  const [loadedSections, setLoadedSections] = useState<NotebookSection[]>(['active']);

  // Draft-related states
  const [libraryNotebookTitleEdit, setLibraryNotebookTitleEdit] = useState(false);
  const [draftTitle, setDraftTitle] = useState<string>('');
  const [draftContent, setDraftContent] = useState<string>('');
  const draftAutoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-save draft content with debounce
  const autoSaveDraft = useCallback(async (notebookId: string, title: string, content: string) => {
    try {
      const updatedNotebook = await updateDraftContent(notebookId, title, content);
      if (updatedNotebook) {
        setSelectedNotebook(updatedNotebook);
        console.log('Draft auto-saved');
      }
    } catch (error) {
      console.error('Failed to auto-save draft:', error);
    }
  }, [updateDraftContent]);

  useEffect(() => {
    setLibraryNotebookTitleEdit(false);
  }, [selectedNotebook?.id]);

  const applyNotebookHeaderTitle = useCallback(
    (nextTitle: string) => {
      if (!selectedNotebook) return;
      const kind = inferNotebookDraftKind(selectedNotebook);
      const nextBody = patchOrEnsurePortalPrimaryTitle(draftContent, nextTitle, selectedNotebook.id, kind);
      setDraftTitle(nextTitle);
      setDraftContent(nextBody);
      if (draftAutoSaveTimeoutRef.current) {
        clearTimeout(draftAutoSaveTimeoutRef.current);
      }
      draftAutoSaveTimeoutRef.current = setTimeout(() => {
        void autoSaveDraft(selectedNotebook.id, nextTitle, nextBody);
      }, 2000);
    },
    [selectedNotebook, draftContent, autoSaveDraft]
  );

  const beginPodcastTitleEdit = useCallback(() => {
    if (!currentPodcast) return;
    const ep = parseOrDefaultPodcastEpisode(
      currentPodcast.content || '',
      currentPodcast.id || '',
      currentPodcast.title,
      currentPodcast.summary
    );
    if (ep.ready_for_publishing) {
      setSnackbarMessage('This episode is marked ready to publish and cannot be edited.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setLibraryPodcastTitleEdit(true);
  }, [currentPodcast]);

  // Handle section change (active/archived/trash)
  const handleSectionChange = (section: NotebookSection) => {
    setSelectedSection(section);
    
    // Check if we've already loaded this section
    if (!loadedSections.includes(section)) {
      // Fetch notebooks for the selected section if not yet loaded
      fetchNotebooks().then(() => {
        // Mark this section as loaded
        setLoadedSections(prev => [...prev, section]);
      });
    }
  };

  // Handle section change for podcasts (active/archived)
  const handlePodcastSectionChange = (section: PodcastSection) => {
    setSelectedPodcastSection(section);
  };

  // Handle article feedback
  const handleArticleFeedback = async (clusterId: string, articleUrl: string) => {
    setArticleFeedbackError('');
    setArticleFeedbackSuccess('');

    // Toggle first so the user sees the color change right away:
    const isIncorrect = incorrectArticles.includes(articleUrl);
    setIncorrectArticles((prev) =>
      isIncorrect
        ? prev.filter((url) => url !== articleUrl)
        : [...prev, articleUrl]
    );

    // Then attempt your request
    try {
      await api.post('/api/feedback/articles/mismatch', {
        cluster_id: clusterId,
        article_url: articleUrl,
        mismatch: !isIncorrect
      });
      setArticleFeedbackSuccess('Feedback submitted successfully!');
      setShowArticleFeedbackAlert(true);
    } catch (error: any) {
      setArticleFeedbackError(error.message || 'An unexpected error occurred.');
      setShowArticleFeedbackAlert(true);
    }
  };

  // Podcast functions
  const hydratePodcastEditorState = (podcast: PodcastData) => {
    const ep = parseOrDefaultPodcastEpisode(
      podcast.content || '',
      podcast.id || '',
      podcast.title,
      podcast.summary
    );
    setPodcastEpisodePayload(ep);
    setEditedTitle(ep.title);
    const body = (ep.show || '').trim() || (ep.script || '').trim() || '';
    setEditedBody(body);
    setHasUnsavedChanges(false);
    setLibraryPodcastTitleEdit(false);
  };

  const handleOpenPodcast = (podcast: PodcastData) => {
    setViewDialogOpen(false);
    setSelectedNotebook(null);
    const mergedClusters = resolvePodcastClustersFromLibrary(podcast, notebooksFromContext);
    setCurrentPodcast({ ...podcast, clusters: mergedClusters });
    setShowPodcastContent(true);
    setPodcastContextError(null);
    setPodcastGenClusterTitle(null);
    hydratePodcastEditorState({ ...podcast, clusters: mergedClusters });
  };

  const handleNewPodcastDraft = async () => {
    setViewDialogOpen(false);
    setSelectedNotebook(null);
    const p = await createBlankPodcast();
    if (!p?.id) return;
    const merged = { ...p, clusters: resolvePodcastClustersFromLibrary(p, notebooksFromContext) };
    setCurrentPodcast(merged);
    hydratePodcastEditorState(merged);
    setShowPodcastContent(true);
    setPodcastContextError(null);
    setPodcastGenClusterTitle(null);
  };

  const applyPodcastHeaderTitle = (nextTitle: string) => {
    setEditedTitle(nextTitle);
    setPodcastEpisodePayload((prev) => (prev ? { ...prev, title: nextTitle } : prev));
    setHasUnsavedChanges(true);
  };

  const handleSavePodcastDraft = async () => {
    if (!currentPodcast?.id || !podcastEpisodePayload) return;

    try {
      const merged: PortalPodcastEpisodePayload = {
        ...podcastEpisodePayload,
        id: currentPodcast.id,
        title: editedTitle,
        show: editedBody,
        script: editedBody,
      };
      const serialized = serializePodcastEpisodeDraftForSave(merged);
      const summary = editedBody.trim().slice(0, 500) || editedTitle;

      const success = await updatePodcast(currentPodcast.id, {
        title: editedTitle,
        summary,
        content: serialized,
        visibility: currentPodcast.visibility || 'private',
      });

      if (success) {
        setCurrentPodcast({
          ...currentPodcast,
          title: editedTitle,
          summary,
          content: serialized,
        });
        setPodcastEpisodePayload(merged);
        await fetchPodcasts();
        setSnackbarMessage('Draft saved successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setHasUnsavedChanges(false);
      } else {
        setSnackbarMessage('Failed to save draft');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error saving podcast draft:', error);
      setSnackbarMessage(error?.message || 'Failed to save draft');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handlePodcastLinkedWikiEntitiesChange = useCallback(
    async (next: WikiConcept[]) => {
      if (!currentPodcast?.id || !podcastEpisodePayload) return;
      const merged: PortalPodcastEpisodePayload = {
        ...podcastEpisodePayload,
        linked_wiki_entities: next,
      };
      setPodcastEpisodePayload(merged);
      const serialized = serializePodcastEpisodeDraftForSave(merged);
      const summary = editedBody.trim().slice(0, 500) || editedTitle;
      const success = await updatePodcast(currentPodcast.id, {
        title: editedTitle,
        summary,
        content: serialized,
        visibility: currentPodcast.visibility || 'private',
      });
      if (success) {
        setCurrentPodcast((prev) =>
          prev && prev.id === currentPodcast.id ? { ...prev, content: serialized, summary } : prev
        );
      }
    },
    [currentPodcast, podcastEpisodePayload, editedBody, editedTitle, updatePodcast]
  );

  const handleMarkPodcastReadyFromDrawer = async () => {
    if (!currentPodcast?.id || !podcastEpisodePayload) return;

    const ep0 = parseOrDefaultPodcastEpisode(
      currentPodcast.content || '',
      currentPodcast.id,
      currentPodcast.title,
      currentPodcast.summary
    );
    if (ep0.ready_for_publishing) {
      return;
    }

    try {
      const merged: PortalPodcastEpisodePayload = {
        ...podcastEpisodePayload,
        id: currentPodcast.id,
        title: editedTitle,
        show: editedBody,
        script: editedBody,
        ready_for_publishing: true,
      };
      const serialized = serializePodcastEpisodeDraftForSave(merged);
      const summary = editedBody.trim().slice(0, 500) || editedTitle;

      const success = await updatePodcast(currentPodcast.id, {
        title: editedTitle,
        summary,
        content: serialized,
        visibility: currentPodcast.visibility || 'private',
      });

      if (success) {
        setCurrentPodcast({
          ...currentPodcast,
          title: editedTitle,
          summary,
          content: serialized,
        });
        setPodcastEpisodePayload(merged);
        await fetchPodcasts();
        setSnackbarMessage('Episode marked ready for publishing (editing is now locked).');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setHasUnsavedChanges(false);
      } else {
        setSnackbarMessage('Failed to update episode status');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error marking podcast ready:', error);
      setSnackbarMessage(error?.message || 'Failed to update episode status');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  /** Returns false when the user cancels closing (generation running / unsaved changes). */
  const handleClosePodcastContent = (): boolean => {
    if (isPodcastGenerating) {
      if (!window.confirm('Draft generation is still in progress. Are you sure you want to cancel?')) {
        return false;
      }
      setIsPodcastGenerating(false);
      setShowPodcastContent(false);
      setGeneratePodcastDialogOpen(false);
      setPodcastGenClusterTitle(null);
    } else if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        return false;
      }
      setHasUnsavedChanges(false);
      setShowPodcastContent(false);
      setLibraryPodcastTitleEdit(false);
      setGeneratePodcastDialogOpen(false);
      setPodcastGenClusterTitle(null);
    } else {
      if (podcastContextError) {
        console.log('Podcast generation error before closing:', podcastContextError);
      }

      setShowPodcastContent(false);
      setLibraryPodcastTitleEdit(false);
      setGeneratePodcastDialogOpen(false);
      setPodcastGenClusterTitle(null);
    }
    return true;
  };

  const podcastLinkedArticleNotebook = useMemo(() => {
    if (!currentPodcast?.linked_notebook_ids?.length) return null;
    for (const nid of currentPodcast.linked_notebook_ids) {
      const nb = notebooksFromContext.find((n) => n.id === nid && inferNotebookDraftKind(n) === 'article');
      if (nb) return nb;
    }
    return null;
  }, [currentPodcast?.linked_notebook_ids, notebooksFromContext]);

  const podcastContrastingViews = useMemo((): ContrastingView[] | null => {
    const merged = mergePodcastInsightClusters(currentPodcast, notebooksFromContext);
    const acc: ContrastingView[] = [];
    for (const cl of merged) {
      for (const v of cl.contrasting_views || []) {
        acc.push(v);
      }
    }
    return acc.length ? acc : null;
  }, [currentPodcast, notebooksFromContext]);

  const handleRequestGeneratePodcastEpisode = () => {
    if (!podcastGenClusterTitle) {
      setSnackbarMessage('Select a cluster to generate an episode from.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setGeneratePodcastDialogOpen(true);
  };

  const handleConfirmGeneratePodcast = async (opts: { duration: number; language: string; visibility: string }) => {
    setGeneratePodcastDialogOpen(false);
    const pid = currentPodcast?.id;
    /** Same merge as Insights → Related clusters (direct on episode + linked library drafts). */
    const cluster = mergePodcastInsightClusters(currentPodcast, notebooksFromContext).find(
      (c) => c.title === podcastGenClusterTitle
    );
    if (!pid || !cluster) {
      setSnackbarMessage('Selected cluster was not found on this episode.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const nb = podcastLinkedArticleNotebook;
    const sourceDraftKind = nb ? inferNotebookDraftKind(nb) : undefined;

    setIsPodcastGenerating(true);
    setPodcastContextError(null);

    try {
      const clusterContent = [
        {
          title: cluster.title,
          detailed_summary: cluster.detailed_summary,
          article_text: cluster.article_text || '',
          contrasting_views: cluster.contrasting_views || [],
        },
      ];

      const podcastData = await generatePodcast(
        clusterContent,
        opts.duration,
        opts.language,
        opts.visibility,
        nb?.id,
        nb?.name,
        sourceDraftKind
      );

      if (podcastData) {
        // API job payload often omits links/clusters; keep what the user already had on this episode.
        const prev = currentPodcast;
        const keepLinks =
          podcastData.linked_notebook_ids && podcastData.linked_notebook_ids.length > 0
            ? podcastData.linked_notebook_ids
            : prev?.linked_notebook_ids ?? podcastData.linked_notebook_ids ?? [];
        const keepClusterIds =
          podcastData.cluster_ids && podcastData.cluster_ids.length > 0
            ? podcastData.cluster_ids
            : prev?.cluster_ids ?? podcastData.cluster_ids ?? [];
        const merged: PodcastData = {
          ...podcastData,
          linked_notebook_ids: keepLinks,
          cluster_ids: keepClusterIds,
        };
        let clusters = resolvePodcastClustersFromLibrary(merged, notebooksFromContext);
        if (!clusters.length && prev?.clusters?.length) {
          clusters = prev.clusters;
        }
        merged.clusters = clusters;
        setCurrentPodcast(merged);
        hydratePodcastEditorState(merged);
      }
    } catch (error: any) {
      console.error('Error generating draft:', error);
      setSnackbarMessage(error.message || 'Failed to generate draft');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsPodcastGenerating(false);
    }
  };

  // Handle archive podcast
  const handleArchivePodcast = async (event: React.MouseEvent, podcast: PodcastData) => {
    event.stopPropagation(); // Prevent opening the podcast
    
    if (!podcast.id) {
      setSnackbarMessage('Cannot archive podcast without ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const success = await changePodcastStatus(podcast.id, 'archived');
      
      if (success) {
        setSnackbarMessage('Podcast archived successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error archiving podcast:', error);
      setSnackbarMessage('Failed to archive podcast: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle restore podcast
  const handleRestorePodcast = async (event: React.MouseEvent, podcast: PodcastData) => {
    event.stopPropagation(); // Prevent opening the podcast
    
    if (!podcast.id) {
      setSnackbarMessage('Cannot restore podcast without ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const success = await changePodcastStatus(podcast.id, 'active');
      
      if (success) {
        setSnackbarMessage('Podcast restored successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error restoring podcast:', error);
      setSnackbarMessage('Failed to restore podcast: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle delete podcast
  const handleDeletePodcast = async (event: React.MouseEvent, podcast: PodcastData) => {
    event.stopPropagation(); // Prevent opening the podcast
    
    if (!podcast.id) {
      setSnackbarMessage('Cannot delete podcast without ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete podcast "${podcast.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const success = await deletePodcast(podcast.id);
      
      if (success) {
        setSnackbarMessage('Podcast deleted successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error deleting podcast:', error);
      setSnackbarMessage('Failed to delete podcast: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle move to trash podcast
  const handleTrashPodcast = async (event: React.MouseEvent, podcast: PodcastData) => {
    event.stopPropagation(); // Prevent opening the podcast
    
    if (!podcast.id) {
      setSnackbarMessage('Cannot move podcast to trash without ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const success = await changePodcastStatus(podcast.id, 'trash');
      
      if (success) {
        setSnackbarMessage('Podcast moved to trash');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error moving podcast to trash:', error);
      setSnackbarMessage('Failed to move podcast to trash: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleTogglePodcastReady = async (podcast: PodcastData) => {
    if (!podcast.id) {
      setSnackbarMessage('Cannot update podcast without ID');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const parsed = parseOrDefaultPodcastEpisode(
      podcast.content,
      podcast.id,
      podcast.title,
      podcast.summary
    );
    if (parsed.ready_for_publishing) {
      return;
    }
    const nextPayload = { ...parsed, ready_for_publishing: true };
    const serialized = serializePodcastEpisodeDraft(nextPayload);

    try {
      const success = await updatePodcast(podcast.id, { content: serialized });
      if (success) {
        setSnackbarMessage('Podcast marked ready to publish.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setCurrentPodcast((prev) => {
          if (!prev || prev.id !== podcast.id) return prev;
          return { ...prev, content: serialized };
        });
      } else {
        setSnackbarMessage('Failed to update podcast');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error toggling podcast ready state:', error);
      setSnackbarMessage(error?.message || 'Failed to update podcast');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleQuickCreateDraft = async () => {
    const kind = mainDraftTabToKind(mainDraftTab);
    if (!kind) return;

    const defaultName =
      kind === 'article'
        ? 'Untitled article'
        : kind === 'recipe'
          ? 'Untitled recipe'
          : kind === 'movie'
            ? 'Untitled movie'
            : 'Untitled event';

    const created = await createNotebook({
      name: defaultName,
      description: '',
      clusters: [],
      status: 'active',
      visibility: 'private',
    });

    if (!created?.id) {
      setSnackbarMessage('Could not create draft');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setNotebookDraftKind(created.id, kind);
    const env = ensurePortalEnvelope('', '', created.id, kind);
    const title = primaryTitleFromEnvelope(env);
    const body = serializePortalDraft(env);
    const updated = await updateDraftContent(created.id, title, body);
    const toOpen = updated ?? { ...created, draft: { title, content: body }, clusters: [], cluster_ids: [] };
    setSnackbarMessage('Draft opened — start editing');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    handleOpenNotebook(toOpen as FrontendNotebook);
  };

  const handleArticlePortalSave = useCallback(
    async (title: string, serialized: string) => {
      const id = selectedNotebook?.id;
      if (!id) return;
      const locked = notebooksFromContext.some(
        (n) => n.id === id && n.draft?.ready_for_publishing
      );
      if (locked) return;
      setDraftTitle(title);
      setDraftContent(serialized);
      const updated = await updateDraftContent(id, title, serialized);
      if (updated && updated.id === selectedNotebook?.id) {
        setSelectedNotebook(updated);
      }
    },
    [notebooksFromContext, selectedNotebook?.id, updateDraftContent]
  );

  // Open notebook details view
  const handleOpenNotebook = async (notebook: FrontendNotebook) => {
    setShowPodcastContent(false);
    setCurrentPodcast(null);
    setSelectedNotebook(notebook);
    setViewDialogOpen(true);
    setPodcastGenClusterTitle(null);
    setDraftGenClusterTitle(null);

    const kind = inferNotebookDraftKind(notebook);
    const raw = notebook.draft?.content || '';
    const titleHint = (notebook.draft?.title || notebook.name || '').trim();
    const env = alignEnvelopeWithNotebookNameIfPlaceholder(
      ensurePortalEnvelope(raw, titleHint, notebook.id, kind),
      notebook.name || ''
    );
    setDraftTitle(primaryTitleFromEnvelope(env));
    setDraftContent(serializePortalDraft(env));
  };

  useEffect(() => {
    const id = peekNotebookToOpen();
    if (!id) return;
    const notebook = notebooksFromContext.find((item) => item.id === id);
    if (!notebook?.draft?.content) return;
    clearNotebookToOpen();
    void handleOpenNotebook(notebook);
    // Open once when the created draft is in the notebook list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebooksFromContext]);

  // Close notebook view
  const handleCloseNotebookView = () => {
    const nb = selectedNotebook;
    const mergedContent = (draftContent || '').trim() || (nb?.draft?.content || '').trim();
    const mergedNotebook =
      nb != null
        ? { ...nb, draft: { ...(nb.draft ?? { title: '', content: '' }), content: mergedContent } }
        : null;
    const shouldDelete = mergedNotebook != null && isDiscardableEmptyLibraryDraft(mergedNotebook);

    setViewDialogOpen(false);
    setPodcastGenClusterTitle(null);
    setDraftGenClusterTitle(null);
    setSelectedClusterForDetails(null);
    setDraftContent('');
    setPodcastEpisodePayload(null);

    if (shouldDelete && mergedNotebook) {
      removeNotebookDraftKind(mergedNotebook.id);
      void deleteNotebook(mergedNotebook.id).then((ok) => {
        // Only clear if nothing else was opened meanwhile (e.g. back/forward straight to another draft).
        if (ok) setSelectedNotebook((prev) => (prev?.id === mergedNotebook.id ? null : prev));
      });
    }
  };

  // Handle save draft content
  const handleSaveDraft = async () => {
    if (!selectedNotebook) return;
    if (selectedNotebook.draft?.ready_for_publishing) {
      setSnackbarMessage('This draft is locked (ready to publish).');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Clear any pending auto-save
    if (draftAutoSaveTimeoutRef.current) {
      clearTimeout(draftAutoSaveTimeoutRef.current);
    }
    
    try {
      const updatedNotebook = await updateDraftContent(selectedNotebook.id, draftTitle, draftContent);
      
      if (updatedNotebook) {
        markNotebookDraftLifetimeSave(selectedNotebook.id);
        // Update the selected notebook with the new timestamps
        setSelectedNotebook(updatedNotebook);
        
        setSnackbarMessage('Draft saved successfully');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to save draft');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error saving draft:', error);
      setSnackbarMessage('Failed to save draft: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle mark draft as ready for publishing
  const handleMarkReadyForPublishing = async () => {
    if (!selectedNotebook) return;

    if (selectedNotebook.draft?.ready_for_publishing) {
      return;
    }

    if (inferNotebookDraftKind(selectedNotebook) === 'recipe') {
      const raw = (selectedNotebook.draft?.content || '').trim();
      const env = tryParsePortalDraft(raw);
      if (env?.kind !== 'recipe' || !recipePayloadMeetsMandatoryFields(env.payload)) {
        setSnackbarMessage(
          'Recipe is incomplete: add title, banner image, course, difficulty, prep time, servings, ingredients, and steps before marking ready.'
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
    }

    try {
      const success = await markDraftReadyForPublishing(selectedNotebook.id, true);

      if (success) {
        setSelectedNotebook({
          ...selectedNotebook,
          draft: {
            ...(selectedNotebook.draft || { title: '', content: '' }),
            ready_for_publishing: true,
            publication_status: 'ready_to_publish',
          },
        });

        setSnackbarMessage('Draft marked as ready for publishing (editing is now locked).');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to update draft status');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      console.error('Error marking draft ready:', error);
      setSnackbarMessage('Failed to update draft status: ' + (error.message || 'Unknown error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Handle opening cluster details
  const handleOpenClusterDetails = (event: React.MouseEvent, cluster: NewsCluster) => {
    // Prevent the click from propagating to the parent (which would navigate away)
    event.stopPropagation();
    
    // Create a deep clone of the cluster and ensure all required properties exist
    const clonedCluster = {
      ...cluster,
      // Ensure all array properties exist
      articles: cluster.articles || [],
      suggested_titles: cluster.suggested_titles || [],
      social_media: cluster.social_media || [],
      contrasting_views: cluster.contrasting_views || [],
      wiki_concepts: cluster.wiki_concepts || [],
      timeline: cluster.timeline || []
    };
    
    setSelectedClusterForDetails(clonedCluster);
  };

  const pollClusterContentJobUntilDone = useCallback(async (jobId: string) => {
    const deadline = Date.now() + 6 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 2800));
      const res = (await api.get(`/api/notebooks/jobs/${jobId}`)) as {
        status?: string;
        error?: string;
        result?: { article_text?: string };
      };
      if (res?.status === 'completed') {
        return { ok: true as const, article_text: res.result?.article_text || '' };
      }
      if (res?.status === 'failed') {
        return { ok: false as const, error: res.error || 'Generation failed' };
      }
    }
    return { ok: false as const, error: 'Timed out waiting for generated draft' };
  }, []);

  const handleGenerateArticleDraftFromCluster = useCallback(async () => {
    if (!selectedNotebook || inferNotebookDraftKind(selectedNotebook) !== 'article') return;
    if (selectedNotebook.draft?.ready_for_publishing) {
      setSnackbarMessage('This draft is locked (ready to publish).');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (!draftGenClusterTitle) {
      setSnackbarMessage('Select a cluster to generate a draft from.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    const cluster = selectedNotebook.clusters?.find((c) => c.title === draftGenClusterTitle);
    if (!cluster) {
      setSnackbarMessage('Selected cluster was not found on this draft.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setArticleClusterGenBusy(true);
    try {
      const jobId = await generateClusterContent(selectedNotebook.id, cluster, 'English');
      if (!jobId) {
        setSnackbarMessage('Could not start draft generation.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      const outcome = await pollClusterContentJobUntilDone(jobId);
      if (!outcome.ok || !outcome.article_text?.trim()) {
        const msg = !outcome.ok ? outcome.error : 'No article body was returned.';
        setSnackbarMessage(msg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      const merged = mergeClusterArticleHtmlIntoArticleDraft(
        draftContent,
        outcome.article_text,
        selectedNotebook.id,
        draftTitle,
        'article'
      );
      setDraftContent(merged);
      const updated = await updateDraftContent(selectedNotebook.id, draftTitle, merged);
      if (updated && updated.id === selectedNotebook.id) {
        setSelectedNotebook(updated);
      }
      setSnackbarMessage('Draft body updated from the selected cluster.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error: any) {
      console.error('Article draft from cluster failed:', error);
      setSnackbarMessage(error?.message || 'Failed to generate draft');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setArticleClusterGenBusy(false);
    }
  }, [
    draftContent,
    draftGenClusterTitle,
    draftTitle,
    generateClusterContent,
    pollClusterContentJobUntilDone,
    selectedNotebook,
    updateDraftContent,
  ]);

  useEffect(() => {
    if (!selectedClusterForDetails || !selectedNotebook) return;
    const fresh = selectedNotebook.clusters?.find((c) => c.title === selectedClusterForDetails.title);
    if (!fresh) return;
    setSelectedClusterForDetails((prev) => {
      if (!prev || prev.title !== fresh.title) return prev;
      const next = {
        ...fresh,
        articles: fresh.articles || [],
        suggested_titles: fresh.suggested_titles || [],
        social_media: fresh.social_media || [],
        contrasting_views: fresh.contrasting_views || [],
        wiki_concepts: fresh.wiki_concepts || [],
        timeline: fresh.timeline || [],
      };
      const sig = (c: NewsCluster) =>
        `${(c.article_text || '').length}|${(c.timeline || []).length}|${(c.wiki_concepts || []).length}|${(c.articles || []).length}|${(c.detailed_summary || '').length}`;
      if (sig(prev) === sig(next)) return prev;
      return next;
    });
    // Intentionally depend on cluster list identity, not full `selectedClusterForDetails`, to avoid update loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNotebook?.id, selectedNotebook?.clusters, selectedClusterForDetails?.title]);

  useEffect(() => {
    if (!selectedClusterForDetails || !currentPodcast || !showPodcastContent) return;
    const fresh = currentPodcast.clusters?.find((c) => c.title === selectedClusterForDetails.title);
    if (!fresh) return;
    setSelectedClusterForDetails((prev) => {
      if (!prev || prev.title !== fresh.title) return prev;
      const next = {
        ...fresh,
        articles: fresh.articles || [],
        suggested_titles: fresh.suggested_titles || [],
        social_media: fresh.social_media || [],
        contrasting_views: fresh.contrasting_views || [],
        wiki_concepts: fresh.wiki_concepts || [],
        timeline: fresh.timeline || [],
      };
      const sig = (c: NewsCluster) =>
        `${(c.article_text || '').length}|${(c.timeline || []).length}|${(c.wiki_concepts || []).length}|${(c.articles || []).length}|${(c.detailed_summary || '').length}`;
      if (sig(prev) === sig(next)) return prev;
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPodcast?.id, currentPodcast?.clusters, selectedClusterForDetails?.title, showPodcastContent]);

  useEffect(() => {
    if (!currentPodcast?.id || !showPodcastContent) return;
    const fromCtx = podcasts.find((p) => p.id === currentPodcast.id);
    if (!fromCtx) return;
    setCurrentPodcast((prev) => {
      if (!prev || prev.id !== fromCtx.id) return prev;
      const idsEq = JSON.stringify(prev.cluster_ids || []) === JSON.stringify(fromCtx.cluster_ids || []);
      const clLenEq = (prev.clusters || []).length === (fromCtx.clusters || []).length;
      if (idsEq && clLenEq) return prev;
      return {
        ...prev,
        ...fromCtx,
        clusters: fromCtx.clusters ?? prev.clusters,
        cluster_ids: fromCtx.cluster_ids ?? prev.cluster_ids,
      };
    });
  }, [podcasts, currentPodcast?.id, showPodcastContent]);

  const sectionNotebooks = notebooksFromContext.filter(notebook => notebook.status === selectedSection);
  const kindForNotebookList = mainDraftTabToKind(mainDraftTab);
  const filteredNotebooks =
    kindForNotebookList === null
      ? []
      : sectionNotebooks.filter((n) => {
          if (inferNotebookDraftKind(n) !== kindForNotebookList) return false;
          if (isDiscardableEmptyLibraryDraft(n)) return false;
          if (kindForNotebookList === 'article' && !qualifiesAsLibraryArticleListItem(n)) return false;
          return true;
        });

  // Filter podcasts by selected section and sort by date (newest first)
  const filteredPodcasts = podcasts
    .filter(podcast => {
      if (selectedPodcastSection === 'active') {
        return podcast.status !== 'archived' && podcast.status !== 'trash';
      } else if (selectedPodcastSection === 'archived') {
        return podcast.status === 'archived';
      } else if (selectedPodcastSection === 'trash') {
        return podcast.status === 'trash';
      }
      return true;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const libraryDrawerOpen = Boolean(
    (selectedNotebook && viewDialogOpen) || (currentPodcast && showPodcastContent)
  );

  // When selected notebook changes in the context menu, update the local state
  useEffect(() => {
    const id = selectedNotebook?.id;
    if (!id) return;
    const updatedNotebook = notebooksFromContext.find((n) => n.id === id);
    if (updatedNotebook) {
      setSelectedNotebook(updatedNotebook);
    } else {
      setSelectedNotebook(null);
    }
  }, [notebooksFromContext, selectedNotebook?.id]);

  /** Returns false when the user cancels closing. */
  const handleLibraryDrawerClose = (): boolean => {
    if (selectedNotebook && viewDialogOpen) {
      handleCloseNotebookView();
      return true;
    }
    return handleClosePodcastContent();
  };

  // ---- URL sync: /library[/<tab>], /library/drafts/:id, /library/podcasts/:id (see routing/appRoutes.ts) ----
  const navigate = useNavigate();
  const location = useLocation();
  const route = parseAppLocation(location.pathname);
  const routeItemId = route?.view === 'library' ? route.itemId : undefined;
  const routeIsPodcast = Boolean(routeItemId) && route?.section === 'podcasts';
  const routeTabIndex =
    route?.view === 'library' && route.section && !routeItemId ? LIBRARY_TABS.indexOf(route.section as LibraryTab) : -1;

  const openItemPath =
    selectedNotebook && viewDialogOpen
      ? libraryDraftPath(selectedNotebook.id)
      : currentPodcast?.id && showPodcastContent
        ? libraryPodcastPath(currentPodcast.id)
        : null;
  const desiredPath = openItemPath ?? libraryTabPath(LIBRARY_TABS[mainDraftTab] ?? 'articles');

  /** Item id from a deep link that hasn't been opened yet (waiting for the list to load). */
  const pendingItemRef = useRef<string | null>(null);

  // State → URL: opening/closing an item or switching tabs updates the address bar.
  const prevDesiredPathRef = useRef(desiredPath);
  useEffect(() => {
    if (prevDesiredPathRef.current === desiredPath) return;
    prevDesiredPathRef.current = desiredPath;
    // While a deep link is resolving (e.g. switching to the Podcasts tab to load it), keep its URL.
    if (pendingItemRef.current) return;
    if (location.pathname !== desiredPath) navigate(desiredPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react only to state changes, not URL changes
  }, [desiredPath]);

  // URL → state: deep links and back/forward. Only acts when the path changes; an item link waits
  // for the list to load (pendingItemRef) so it can open it or report it missing.
  const prevPathnameRef = useRef<string | null>(null);
  useEffect(() => {
    const pathChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    if (pathChanged) {
      pendingItemRef.current = routeItemId ?? null;
      if (routeTabIndex >= 0 && routeTabIndex !== mainDraftTab) setMainDraftTab(routeTabIndex);
      if (openItemPath && openItemPath !== location.pathname) {
        // Back/forward away from the open item (to the list or to another item): close it first so
        // unsaved-changes prompts and empty-draft cleanup run. If the user cancels, restore its URL
        // (replace: the entry they navigated to is dropped, which keeps history and screen in sync).
        if (!handleLibraryDrawerClose()) {
          pendingItemRef.current = null;
          navigate(openItemPath, { replace: true });
          return;
        }
        if (!routeItemId) return;
      }
    }

    const itemId = pendingItemRef.current;
    if (!itemId) return;
    const showMissing = (what: string) => {
      pendingItemRef.current = null;
      setSnackbarMessage(`That ${what} wasn't found. It may belong to another analytic profile, or it was deleted.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      navigate(libraryTabPath(routeIsPodcast ? 'podcasts' : 'articles'), { replace: true });
    };

    if (routeIsPodcast) {
      if (currentPodcast?.id === itemId && showPodcastContent) {
        pendingItemRef.current = null;
        return;
      }
      const podcast = podcasts.find((p) => p.id === itemId);
      if (podcast) {
        pendingItemRef.current = null;
        setMainDraftTab(LIBRARY_TABS.indexOf('podcasts'));
        handleOpenPodcast(podcast);
      } else if (!podcastsHaveLoaded) {
        setMainDraftTab(LIBRARY_TABS.indexOf('podcasts')); // the podcasts tab triggers the fetch
      } else if (!isLoading) {
        showMissing('podcast');
      }
      return;
    }

    if (selectedNotebook?.id === itemId && viewDialogOpen && openItemPath === location.pathname) {
      pendingItemRef.current = null;
      return;
    }
    const notebook = notebooksFromContext.find((n) => n.id === itemId);
    if (notebook) {
      pendingItemRef.current = null;
      setMainDraftTab(TAB_FOR_DRAFT_KIND[inferNotebookDraftKind(notebook)]);
      void handleOpenNotebook(notebook);
    } else if (notebooksHaveLoaded && !notebooksLoading) {
      showMissing('draft');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers are recreated each render; inputs are listed
  }, [location.pathname, notebooksFromContext, notebooksHaveLoaded, notebooksLoading, podcasts, podcastsHaveLoaded, isLoading]);

  useEffect(() => {
    if (mainDraftTab === 1 && podcasts.length === 0) {
      fetchPodcasts();
    }
  }, [mainDraftTab, fetchPodcasts, podcasts.length]);

  const handleStructuredSave = useCallback(
    async (title: string, body: string) => {
      const id = selectedNotebook?.id;
      if (!id) return;
      const locked = notebooksFromContext.some(
        (n) => n.id === id && n.draft?.ready_for_publishing
      );
      if (locked) return;
      setDraftTitle(title);
      setDraftContent(body);
      const updated = await updateDraftContent(id, title, body);
      if (updated && updated.id === selectedNotebook?.id) {
        setSelectedNotebook(updated);
      }
    },
    [notebooksFromContext, selectedNotebook?.id, updateDraftContent]
  );

  const openNotebookKind = selectedNotebook ? inferNotebookDraftKind(selectedNotebook) : 'article';
  const isArticleNotebookOpen = openNotebookKind === 'article';
  const draftContentLocked = Boolean(selectedNotebook?.draft?.ready_for_publishing);
  const podcastAccessReadOnly = Boolean(
    currentPodcast &&
      parseOrDefaultPodcastEpisode(
        currentPodcast.content || '',
        currentPodcast.id || '',
        currentPodcast.title,
        currentPodcast.summary
      ).ready_for_publishing
  );

  const activeNotebookChoices = getActiveNotebooks();

  const notebookDraftAccessBar =
    selectedNotebook && viewDialogOpen ? (
      <DraftAccessBar
        variant="notebook"
        notebook={selectedNotebook}
        creatorDisplayName={creatorDisplayName}
        profiles={availableProfiles}
        activeNotebooks={activeNotebookChoices}
        allNotebooks={notebooksFromContext}
        readOnly={draftContentLocked}
        onSave={async (u) => {
          const id = selectedNotebook.id;
          const ok = await updateNotebook(id, u);
          if (ok) {
            setSelectedNotebook((prev) => (prev && prev.id === id ? { ...prev, ...u } : prev));
          }
        }}
      />
    ) : null;

  const podcastDrawerAccessBar =
    currentPodcast && showPodcastContent && !isPodcastGenerating && currentPodcast.id ? (
      <DraftAccessBar
        variant="podcast"
        podcast={currentPodcast}
        creatorDisplayName={creatorDisplayName}
        profiles={availableProfiles}
        activeNotebooks={activeNotebookChoices}
        allNotebooks={notebooksFromContext}
        readOnly={podcastAccessReadOnly}
        onSave={async (u) => {
          const id = currentPodcast.id;
          if (!id) return;
          const ok = await updatePodcast(id, u);
          if (ok) {
            setCurrentPodcast((prev) => (prev && prev.id === id ? { ...prev, ...u } : prev));
          }
        }}
      />
    ) : null;

  const showDraftFooterActions = Boolean(selectedNotebook && viewDialogOpen);

  const libraryDrawerUsesWidePaper =
    Boolean(selectedNotebook && viewDialogOpen) ||
    Boolean(currentPodcast && showPodcastContent);

  const notebookKindIconEl =
    openNotebookKind === 'recipe' ? (
      <RestaurantIcon sx={{ fontSize: 24 }} />
    ) : openNotebookKind === 'movie' ? (
      <MovieIcon sx={{ fontSize: 24 }} />
    ) : openNotebookKind === 'event' ? (
      <EventIcon sx={{ fontSize: 24 }} />
    ) : (
      <ArticleIcon sx={{ fontSize: 24 }} />
    );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ bgcolor: '#0a0a0a', minHeight: 'calc(100vh - 80px)', px: { xs: 2, md: 3 }, pt: 2, pb: 3 }}>
        {/* Tabs are router links (no onChange): the URL → state sync below picks the tab, and modified
            clicks only open a new browser tab. */}
        <Tabs
          value={mainDraftTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            marginBottom: 2,
            '& .MuiTab-root': {
              color: '#a1a1aa',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#fafafa',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#60a5fa',
            },
          }}
        >
          {LIBRARY_TABS.map((tab, i) => {
            const { Icon, label } = LIBRARY_TAB_LABELS[tab];
            return (
              <Tab
                key={tab}
                value={i}
                component={RouterLink}
                to={libraryTabPath(tab)}
                onClick={i === mainDraftTab ? ignorePlainClick : undefined}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Icon sx={{ fontSize: 18 }} />
                    {label}
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        {/* Articles, Recipes, Movies, Events — notebook lists */}
        {mainDraftTab !== 1 && (
          <Box>
            {/* Section tabs (active/archived/trash) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Button 
                  variant={selectedSection === 'active' ? 'contained' : 'outlined'} 
                  onClick={() => handleSectionChange('active')}
                  sx={{ 
                    mr: 1,
                    bgcolor: selectedSection === 'active' ? '#60a5fa' : 'transparent',
                    color: selectedSection === 'active' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedSection === 'active' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Active
                </Button>
                <Button 
                  variant={selectedSection === 'archived' ? 'contained' : 'outlined'} 
                  onClick={() => handleSectionChange('archived')}
                  sx={{ 
                    mr: 1,
                    bgcolor: selectedSection === 'archived' ? '#60a5fa' : 'transparent',
                    color: selectedSection === 'archived' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedSection === 'archived' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Archived
                </Button>
                <Button 
                  variant={selectedSection === 'trash' ? 'contained' : 'outlined'} 
                  onClick={() => handleSectionChange('trash')}
                  sx={{ 
                    bgcolor: selectedSection === 'trash' ? '#60a5fa' : 'transparent',
                    color: selectedSection === 'trash' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedSection === 'trash' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Trash
                </Button>
              </Box>
              
              {/* Create new notebook button - only show in active section */}
              {selectedSection === 'active' && (
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => void handleQuickCreateDraft()}
                  sx={{
                    bgcolor: '#60a5fa',
                    color: '#0a0a0a',
                    '&:hover': {
                      bgcolor: '#3b82f6'
                    }
                  }}
                >
                  {mainDraftTab === 0 && 'New article draft'}
                  {mainDraftTab === 2 && 'New recipe draft'}
                  {mainDraftTab === 3 && 'New movie draft'}
                  {mainDraftTab === 4 && 'New event draft'}
                </Button>
              )}
            </Box>

            {/* Main content */}
            <Box sx={{ pt: 1 }}>
              {filteredNotebooks.length === 0 ? (
                <Typography variant="body1" sx={{ textAlign: 'center', py: 4, color: '#a1a1aa' }}>
                  {mainDraftTab === 0 &&
                    'No article drafts in this section. Create one to draft from clusters and run AI extras.'}
                  {mainDraftTab === 2 && 'No recipe drafts here. Recipes use structured ingredients and steps for your portal.'}
                  {mainDraftTab === 3 && 'No movie listings yet. Draft decks, reviews, and where-to-watch in one place.'}
                  {mainDraftTab === 4 && 'No event drafts yet. Capture schedule, venue, and ticket links for calendars.'}
                </Typography>
              ) : (
                <List>
                  {filteredNotebooks.map((notebook, index) => (
                    <NotebookListItem
                      key={notebook.id}
                      notebook={notebook}
                      onClick={handleOpenNotebook}
                      divider={index < filteredNotebooks.length - 1}
                    />
                  ))}
                </List>
              )}
            </Box>

          </Box>
        )}

        {/* Content for PODCASTS */}
        {mainDraftTab === 1 && (
          <Box>
            {/* Section tabs for podcast (active/archived/trash) */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Button 
                  variant={selectedPodcastSection === 'active' ? 'contained' : 'outlined'} 
                  onClick={() => handlePodcastSectionChange('active')}
                  sx={{ 
                    mr: 1,
                    bgcolor: selectedPodcastSection === 'active' ? '#60a5fa' : 'transparent',
                    color: selectedPodcastSection === 'active' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedPodcastSection === 'active' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Active
                </Button>
                <Button 
                  variant={selectedPodcastSection === 'archived' ? 'contained' : 'outlined'} 
                  onClick={() => handlePodcastSectionChange('archived')}
                  sx={{ 
                    mr: 1,
                    bgcolor: selectedPodcastSection === 'archived' ? '#60a5fa' : 'transparent',
                    color: selectedPodcastSection === 'archived' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedPodcastSection === 'archived' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Archived
                </Button>
                <Button 
                  variant={selectedPodcastSection === 'trash' ? 'contained' : 'outlined'} 
                  onClick={() => handlePodcastSectionChange('trash')}
                  sx={{ 
                    bgcolor: selectedPodcastSection === 'trash' ? '#60a5fa' : 'transparent',
                    color: selectedPodcastSection === 'trash' ? '#0a0a0a' : '#fafafa',
                    borderColor: '#3f3f46',
                    '&:hover': {
                      bgcolor: selectedPodcastSection === 'trash' ? '#3b82f6' : '#27272a'
                    }
                  }}
                >
                  Trash
                </Button>
              </Box>
              {selectedPodcastSection === 'active' && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => void handleNewPodcastDraft()}
                  sx={{
                    bgcolor: '#60a5fa',
                    color: '#0a0a0a',
                    '&:hover': { bgcolor: '#3b82f6' },
                  }}
                >
                  New podcast draft
                </Button>
              )}
            </Box>

            {isLoading && podcasts.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress size={30} sx={{ mr: 2, color: '#60a5fa' }} />
                <Typography variant="h6" sx={{ color: '#fafafa' }}>
                  Loading podcasts...
                </Typography>
              </Box>
            ) : filteredPodcasts.length === 0 ? (
              <Box textAlign="center" py={5}>
                <PodcastsIcon sx={{ fontSize: 60, color: '#a1a1aa', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fafafa' }}>
                  No podcasts in this section
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 3 }}>
                  {selectedPodcastSection === 'active' ? (
                    "Create podcasts from your saved notebooks by selecting clusters and using the 'Create Podcast' button."
                  ) : selectedPodcastSection === 'archived' ? (
                    "You don't have any archived podcasts yet."
                  ) : (
                    "Your trash is empty."
                  )}
                </Typography>
              </Box>
            ) : (
              <>
                {isLoading && (
                  <Box 
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center"
                    position="fixed"
                    top="64px"
                    left="50%"
                    bgcolor="rgba(24, 24, 27, 0.95)"
                    zIndex={100}
                    py={1}
                    px={2}
                    sx={{ 
                      transform: 'translateX(-50%)',
                      borderRadius: '4px',
                      border: '1px solid #27272a',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                    }}
                  >
                    <CircularProgress size={20} sx={{ mr: 1, color: '#60a5fa' }} />
                    <Typography variant="body2" sx={{ color: '#fafafa' }}>
                      Refreshing...
                    </Typography>
                  </Box>
                )}
                <List sx={{ py: 0 }}>
                  {filteredPodcasts.map((podcast, index) => (
                    <PodcastListItem
                      key={podcast.id ?? `podcast-${index}`}
                      podcast={podcast}
                      divider={index < filteredPodcasts.length - 1}
                      onOpen={handleOpenPodcast}
                      onArchive={handleArchivePodcast}
                      onTrash={handleTrashPodcast}
                      onRestore={handleRestorePodcast}
                      onDelete={handleDeletePodcast}
                      onToggleReady={handleTogglePodcastReady}
                    />
                  ))}
                </List>
              </>
            )}
          </Box>
        )}

        {/* Notebook + podcast detail: right drawer (same pattern as flock drafts) */}
        <Drawer
            anchor="right"
            open={libraryDrawerOpen}
            onClose={handleLibraryDrawerClose}
            ModalProps={{
              keepMounted: false,
              disableEnforceFocus: true,
              disableAutoFocus: true,
            }}
            PaperProps={{
              sx: {
                bgcolor: '#0a0a0a',
                borderLeft: '1px solid #27272a',
                width: { xs: '100%', sm: libraryDrawerUsesWidePaper ? '70vw' : '50vw' },
                maxWidth: { xs: '100%', sm: libraryDrawerUsesWidePaper ? 1680 : 960 },
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                borderBottom: '1px solid #27272a',
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                {selectedNotebook && viewDialogOpen ? (
                  <LibraryDrawerTitleChrome
                    kindIcon={notebookKindIconEl}
                    displayTitle={draftTitle || selectedNotebook.name}
                    isEditingTitle={libraryNotebookTitleEdit}
                    onRequestEditTitle={() => setLibraryNotebookTitleEdit(true)}
                    onTitleChange={(t) => applyNotebookHeaderTitle(t)}
                    onTitleBlur={() => setLibraryNotebookTitleEdit(false)}
                    titleDisabled={draftContentLocked}
                    suggestedTitles={selectedNotebook.draft?.suggested_titles ?? undefined}
                    showTitleSuggestions
                    accessBar={notebookDraftAccessBar}
                  />
                ) : currentPodcast && showPodcastContent ? (
                  <LibraryDrawerTitleChrome
                    kindIcon={<PodcastsIcon sx={{ fontSize: 24 }} />}
                    displayTitle={
                      isPodcastGenerating ? 'Generating Podcast…' : editedTitle || currentPodcast?.title || ''
                    }
                    isEditingTitle={Boolean(libraryPodcastTitleEdit && !isPodcastGenerating)}
                    onRequestEditTitle={beginPodcastTitleEdit}
                    onTitleChange={(t) => applyPodcastHeaderTitle(t)}
                    onTitleBlur={() => setLibraryPodcastTitleEdit(false)}
                    titleDisabled={
                      isPodcastGenerating ||
                      Boolean(
                        currentPodcast &&
                          parseOrDefaultPodcastEpisode(
                            currentPodcast.content || '',
                            currentPodcast.id || '',
                            currentPodcast.title,
                            currentPodcast.summary
                          ).ready_for_publishing
                      )
                    }
                    accessBar={podcastDrawerAccessBar}
                  />
                ) : null}
              </Box>
              <Box display="flex" alignItems="center" flexShrink={0}>
                <IconButton onClick={handleLibraryDrawerClose} sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            {selectedNotebook && viewDialogOpen ? (
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                pt: 0,
                px: 2,
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {isArticleNotebookOpen && selectedNotebook && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    mt: 2,
                    /* `overflow: hidden` clips Editor.js inline UI; keep flex shrink without clipping the body. */
                    overflow: 'visible',
                  }}
                >
                  <ArticlePortalDraftPanel
                    key={selectedNotebook.id}
                    notebookId={selectedNotebook.id}
                    draftTitle={draftTitle}
                    draftContent={draftContent}
                    onTitleChange={setDraftTitle}
                    onSaveSerialized={handleArticlePortalSave}
                    readOnly={draftContentLocked}
                    clusters={selectedNotebook.clusters}
                    clustersLoading={notebookClustersLoading}
                    clusterIds={selectedNotebook.cluster_ids}
                    onOpenClusterDetails={handleOpenClusterDetails}
                    isClusterContentGenerating={isClusterContentGenerating}
                    selectedClusterTitleForDraft={draftGenClusterTitle}
                    onSelectClusterTitleForDraft={setDraftGenClusterTitle}
                    onGenerateArticleDraftFromCluster={handleGenerateArticleDraftFromCluster}
                    isGeneratingArticleDraftFromCluster={articleClusterGenBusy}
                    socialMediaPosts={selectedNotebook.draft?.social_media_posts ?? null}
                    contrastingViews={selectedNotebook.draft?.contrasting_views ?? null}
                    linkedNotebookIds={selectedNotebook.linked_notebook_ids || []}
                    allNotebooks={notebooksFromContext}
                    activeNotebooks={activeNotebookChoices}
                    onLinkedNotebookIdsChange={async (ids) => {
                      const id = selectedNotebook.id;
                      const ok = await updateNotebook(id, { linked_notebook_ids: ids });
                      if (ok) {
                        setSelectedNotebook((prev) => (prev && prev.id === id ? { ...prev, linked_notebook_ids: ids } : prev));
                      }
                    }}
                    onAddClustersToDraft={async (clusters) => {
                      const id = selectedNotebook.id;
                      const ok = await addClustersToNotebook(id, clusters);
                      if (ok) {
                        setSelectedNotebook((prev) => {
                          if (!prev || prev.id !== id) return prev;
                          const titles = new Set((prev.clusters || []).map((c) => c.title));
                          const added = clusters
                            .filter((c) => !titles.has(c.title))
                            .map((cluster) => ({
                              ...cluster,
                              articles: cluster.articles || [],
                              suggested_titles: cluster.suggested_titles || [],
                              social_media: cluster.social_media || [],
                              contrasting_views: cluster.contrasting_views || [],
                              wiki_concepts: cluster.wiki_concepts || [],
                              timeline: cluster.timeline || [],
                            }));
                          const nextClusters = [...(prev.clusters || []), ...added];
                          return {
                            ...prev,
                            clusters: nextClusters,
                            cluster_ids: nextClusters.map((c) => c.cluster_id || c.title),
                          };
                        });
                      }
                      return ok;
                    }}
                    onRemoveClusterFromDraft={async (title) => {
                      const id = selectedNotebook.id;
                      const ok = await removeClustersFromNotebook(id, [title]);
                      if (ok) {
                        setSelectedNotebook((prev) => {
                          if (!prev || prev.id !== id) return prev;
                          const nextClusters = (prev.clusters || []).filter((c) => c.title !== title);
                          return {
                            ...prev,
                            clusters: nextClusters,
                            cluster_ids: nextClusters.map((c) => c.cluster_id || c.title),
                          };
                        });
                      }
                      return ok;
                    }}
                    draftLinkedWikiEntities={selectedNotebook.draft?.linked_wiki_entities ?? []}
                    onDraftLinkedWikiEntitiesChange={async (next: WikiConcept[]) => {
                      const nb = selectedNotebook;
                      if (!nb) return;
                      const id = nb.id;
                      const ok = await updateNotebook(id, {
                        name: draftTitle.trim() || nb.name,
                        draft: {
                          ...(nb.draft ?? { title: '', content: '' }),
                          title: draftTitle,
                          content: draftContent || nb.draft?.content || '',
                          linked_wiki_entities: next,
                        },
                      });
                      if (ok) {
                        setSelectedNotebook((prev) => {
                          if (!prev || prev.id !== id) return prev;
                          return {
                            ...prev,
                            name: draftTitle.trim() || prev.name,
                            draft: {
                              ...(prev.draft ?? { title: '', content: '' }),
                              title: draftTitle,
                              content: draftContent || prev.draft?.content || '',
                              linked_wiki_entities: next,
                            },
                          };
                        });
                      }
                    }}
                  />
                </Box>
              )}

              {(openNotebookKind === 'recipe' ||
                openNotebookKind === 'movie' ||
                openNotebookKind === 'event') && (
                <Box sx={{ mt: 2 }}>
                  <StructuredDraftPanel
                    key={selectedNotebook.id}
                    notebookId={selectedNotebook.id}
                    kind={openNotebookKind}
                    draftTitle={draftTitle}
                    draftContent={draftContent}
                    onTitleChange={setDraftTitle}
                    onSaveBody={handleStructuredSave}
                    accessInHeader
                    readOnly={draftContentLocked}
                  />
                </Box>
              )}

            </Box>
            ) : currentPodcast && showPodcastContent ? (
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                pt: 0,
                px: 2,
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            {isPodcastGenerating ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5}>
                <CircularProgress size={60} sx={{ mb: 3, color: '#60a5fa' }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#fafafa' }}>
                  {clusterDraftGenerationLoadingTitle()}
                </Typography>
                <Typography variant="body2" align="center" sx={{ maxWidth: '80%', color: '#a1a1aa' }}>
                  {selectedNotebook
                    ? clusterDraftGenerationLoadingBody(inferNotebookDraftKind(selectedNotebook))
                    : clusterDraftGenerationLoadingBody('article')}
                </Typography>
              </Box>
            ) : podcastContextError ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5}>
                <Alert severity="error" sx={{ width: '100%', mb: 3, bgcolor: '#27272a', color: '#f87171', border: '1px solid #3f3f46' }}>
                  {podcastContextError}
                </Alert>
                <Typography variant="body2" align="center" sx={{ maxWidth: '80%', color: '#a1a1aa' }}>
                  There was an error generating your draft. Try different clusters or check the console for details.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setPodcastContextError(null);
                    if (podcastGenClusterTitle) {
                      setGeneratePodcastDialogOpen(true);
                    } else {
                      setSnackbarMessage('Select a cluster in Insights, then try again.');
                      setSnackbarSeverity('info');
                      setSnackbarOpen(true);
                    }
                  }}
                  sx={{
                    mt: 3,
                    bgcolor: '#60a5fa',
                    color: '#0a0a0a',
                    '&:hover': { bgcolor: '#3b82f6' },
                  }}
                >
                  Try Again
                </Button>
              </Box>
            ) : podcastEpisodePayload && currentPodcast.id ? (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', mt: 2, overflow: 'hidden' }}>
                <PodcastPortalDraftPanel
                  episode={podcastEpisodePayload}
                  onEpisodeChange={(next) => {
                    setPodcastEpisodePayload(next);
                    setHasUnsavedChanges(true);
                  }}
                  bodyText={editedBody}
                  onBodyChange={(t) => {
                    setEditedBody(t);
                    setHasUnsavedChanges(true);
                  }}
                  readOnly={podcastAccessReadOnly}
                  linkRail={
                    currentPodcast.id
                      ? {
                          podcastId: currentPodcast.id,
                          linkedNotebookIds: currentPodcast.linked_notebook_ids || [],
                          allNotebooks: notebooksFromContext,
                          activeNotebooks: activeNotebookChoices,
                          podcastClusterIds: currentPodcast.cluster_ids || [],
                          onLinkedNotebookIdsChange: async (ids) => {
                            const pid = currentPodcast.id;
                            if (!pid) return;
                            const ok = await updatePodcast(pid, { linked_notebook_ids: ids });
                            if (ok) {
                              setCurrentPodcast((prev) =>
                                prev && prev.id === pid ? { ...prev, linked_notebook_ids: ids } : prev
                              );
                            }
                          },
                          clusters: currentPodcast.clusters || [],
                          clustersLoading: false,
                          clusterIds: currentPodcast.cluster_ids,
                          onOpenClusterDetails: handleOpenClusterDetails,
                          isClusterContentGenerating,
                          clusterContentScopeId: podcastLinkedArticleNotebook?.id ?? null,
                          selectedClusterTitleForEpisode: podcastGenClusterTitle,
                          onSelectClusterTitleForEpisode: setPodcastGenClusterTitle,
                          onRequestGeneratePodcastEpisode: handleRequestGeneratePodcastEpisode,
                          isGeneratingPodcastEpisode: isPodcastGenerating,
                          contrastingViews: podcastContrastingViews,
                          onAddClustersToDraft: async (clusters) => {
                            const pid = currentPodcast.id;
                            if (!pid) return false;
                            return addClustersToPodcast(pid, clusters);
                          },
                          onRemoveClusterFromDraft: async (title) => {
                            const pid = currentPodcast.id;
                            if (!pid) return false;
                            return removeClustersFromPodcast(pid, [title]);
                          },
                          draftLinkedWikiEntities: podcastEpisodePayload.linked_wiki_entities,
                          onDraftLinkedWikiEntitiesChange: handlePodcastLinkedWikiEntitiesChange,
                        }
                      : undefined
                  }
                />
              </Box>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" height="200px">
                <Typography sx={{ color: '#a1a1aa' }}>No podcast content available</Typography>
              </Box>
            )}
            </Box>
            ) : null}
            {selectedNotebook && viewDialogOpen ? (
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                borderTop: '1px solid #27272a',
                px: 2,
                py: 1.5,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Box display="flex" gap={2} flexWrap="wrap">
                {showDraftFooterActions && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveDraft}
                      disabled={draftContentLocked}
                      sx={{
                        borderColor: '#3f3f46',
                        color: '#fafafa',
                        '&:hover': {
                          borderColor: '#60a5fa',
                          bgcolor: '#27272a',
                        },
                      }}
                    >
                      Save Draft
                    </Button>

                    <Button
                      variant={draftContentLocked ? 'contained' : 'outlined'}
                      onClick={handleMarkReadyForPublishing}
                      disabled={draftContentLocked}
                      startIcon={<CheckCircleIcon />}
                      sx={{
                        bgcolor: draftContentLocked ? '#d1fae5' : 'transparent',
                        color: draftContentLocked ? '#047857' : '#fafafa',
                        borderColor: draftContentLocked ? '#6ee7b7' : '#3f3f46',
                        '&:hover': {
                          bgcolor: draftContentLocked ? '#d1fae5' : '#27272a',
                          borderColor: draftContentLocked ? '#6ee7b7' : '#60a5fa',
                        },
                        '&.Mui-disabled': {
                          bgcolor: draftContentLocked ? '#d1fae5' : undefined,
                          color: draftContentLocked ? '#047857' : undefined,
                          borderColor: draftContentLocked ? '#6ee7b7' : undefined,
                        },
                      }}
                    >
                      {draftContentLocked ? 'Ready for Publishing (locked)' : 'Mark as Ready'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            ) : currentPodcast && showPodcastContent ? (
            <Box
              sx={{
                bgcolor: '#0a0a0a',
                borderTop: '1px solid #27272a',
                px: 2,
                py: 1.5,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                flexShrink: 0,
              }}
            >
              {!isPodcastGenerating && !podcastContextError && podcastEpisodePayload && currentPodcast.id ? (
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    onClick={handleSavePodcastDraft}
                    disabled={podcastAccessReadOnly}
                    sx={{
                      borderColor: '#3f3f46',
                      color: '#fafafa',
                      '&:hover': {
                        borderColor: '#60a5fa',
                        bgcolor: '#27272a',
                      },
                    }}
                  >
                    Save Draft
                  </Button>

                  <Button
                    variant={podcastAccessReadOnly ? 'contained' : 'outlined'}
                    onClick={handleMarkPodcastReadyFromDrawer}
                    disabled={podcastAccessReadOnly}
                    startIcon={<CheckCircleIcon />}
                    sx={{
                      bgcolor: podcastAccessReadOnly ? '#d1fae5' : 'transparent',
                      color: podcastAccessReadOnly ? '#047857' : '#fafafa',
                      borderColor: podcastAccessReadOnly ? '#6ee7b7' : '#3f3f46',
                      '&:hover': {
                        bgcolor: podcastAccessReadOnly ? '#d1fae5' : '#27272a',
                        borderColor: podcastAccessReadOnly ? '#6ee7b7' : '#60a5fa',
                      },
                      '&.Mui-disabled': {
                        bgcolor: podcastAccessReadOnly ? '#d1fae5' : undefined,
                        color: podcastAccessReadOnly ? '#047857' : undefined,
                        borderColor: podcastAccessReadOnly ? '#6ee7b7' : undefined,
                      },
                    }}
                  >
                    {podcastAccessReadOnly ? 'Ready for Publishing (locked)' : 'Mark as Ready'}
                  </Button>
                </Box>
              ) : null}
            </Box>
            ) : null}
          </Drawer>

        <GeneratePodcastFromClusterDialog
          open={generatePodcastDialogOpen}
          clusterCount={podcastGenClusterTitle ? 1 : 0}
          busy={isPodcastGenerating}
          onClose={() => setGeneratePodcastDialogOpen(false)}
          onConfirm={(opts) => void handleConfirmGeneratePodcast(opts)}
        />

        <LibraryClusterInsightsDrawer
          open={Boolean(selectedClusterForDetails)}
          cluster={selectedClusterForDetails}
          onClose={() => setSelectedClusterForDetails(null)}
          showBackToArticle={Boolean(
            (selectedNotebook && viewDialogOpen && selectedClusterForDetails) ||
              (currentPodcast && showPodcastContent && selectedClusterForDetails)
          )}
          onBackToArticle={() => setSelectedClusterForDetails(null)}
          draftBackLabel={currentPodcast && showPodcastContent ? 'Back to podcast' : 'Back to article'}
          incorrectArticles={incorrectArticles}
          onArticleFeedback={handleArticleFeedback}
          isGeneratingContent={
            selectedClusterForDetails
              ? isClusterContentGenerating(
                  selectedClusterForDetails.title,
                  (selectedNotebook && viewDialogOpen && selectedNotebook.id) ||
                    (currentPodcast && showPodcastContent ? podcastLinkedArticleNotebook?.id : undefined)
                )
              : false
          }
        />

        {/* Notification snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Snackbar for article feedback success/error */}
        <Snackbar
          open={showArticleFeedbackAlert}
          autoHideDuration={6000}
          onClose={() => setShowArticleFeedbackAlert(false)}
        >
          {articleFeedbackError ? (
            <Alert onClose={() => setShowArticleFeedbackAlert(false)} severity="error" sx={{ width: '100%' }}>
              {articleFeedbackError}
            </Alert>
          ) : articleFeedbackSuccess ? (
            <Alert onClose={() => setShowArticleFeedbackAlert(false)} severity="success" sx={{ width: '100%' }}>
              {articleFeedbackSuccess}
            </Alert>
          ) : <></>}
        </Snackbar>

      </Box>
    </LocalizationProvider>
  );
};

export default LayoutLibrary; 